import { useState, useEffect } from 'react';
import { X, PartyPopper } from 'lucide-react';

// Badge icon mapping
const BADGE_ICONS = {
  'Founding Diver': '🏆',
  'Dive Explorer': '🗺️',
  'Dive Legend': '⭐',
  'Dive Connoisseur': '👑',
  'Friend of Tilted': '🤝',
  'Tilted Ambassador': '📣',
  'Word Spreader': '📢',
  'Buzz Builder': '🐝',
  'Dive Ambassador': '🎖️',
  'Tilted Influencer': '👑',
  'Dive Scout': '🔍',
  'Dive Finder': '🗺️'
};

// Confetti particle component
function ConfettiParticle({ delay, left }) {
  const colors = ['#f59e0b', '#22c55e', '#3b82f6', '#ec4899', '#8b5cf6', '#ef4444'];
  const color = colors[Math.floor(Math.random() * colors.length)];
  
  return (
    <div
      className="absolute w-2 h-2 rounded-full animate-confetti"
      style={{
        left: `${left}%`,
        backgroundColor: color,
        animationDelay: `${delay}ms`,
        top: '-10px'
      }}
    />
  );
}

export function BadgeCelebration({ badge, onClose }) {
  const [isVisible, setIsVisible] = useState(false);
  const [confetti, setConfetti] = useState([]);

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 50);
    
    // Generate confetti particles
    const particles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      delay: Math.random() * 1000,
      left: Math.random() * 100
    }));
    setConfetti(particles);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  if (!badge) return null;

  const icon = BADGE_ICONS[badge.name] || badge.icon || '🏅';

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      
      {/* Confetti container */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {confetti.map(p => (
          <ConfettiParticle key={p.id} delay={p.delay} left={p.left} />
        ))}
      </div>

      {/* Modal */}
      <div 
        className={`relative bg-gradient-to-b from-dive-dark to-dive-darker border-2 border-dive-amber rounded-2xl p-6 max-w-sm w-full shadow-2xl transition-all duration-500 ${
          isVisible 
            ? 'opacity-100 scale-100 translate-y-0' 
            : 'opacity-0 scale-75 translate-y-8'
        }`}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
        >
          <X size={20} />
        </button>

        {/* Celebration content */}
        <div className="text-center">
          {/* Party icon */}
          <div className="flex justify-center mb-4">
            <PartyPopper className="w-8 h-8 text-dive-amber animate-bounce" />
          </div>

          {/* Badge icon with animation */}
          <div 
            className={`text-7xl mb-4 transition-all duration-700 delay-200 ${
              isVisible ? 'scale-100 rotate-0' : 'scale-0 rotate-180'
            }`}
          >
            {icon}
          </div>

          {/* Header */}
          <h2 className="text-2xl font-display text-dive-amber mb-2 tracking-wide">
            CONGRATULATIONS!
          </h2>

          {/* Badge name */}
          <h3 className="text-xl font-bold text-white mb-3">
            {badge.name}
          </h3>

          {/* Description */}
          <p className="text-gray-300 text-sm mb-6 leading-relaxed">
            {badge.description}
          </p>

          {/* Earned date if available */}
          {badge.earned_at && (
            <p className="text-gray-500 text-xs mb-4">
              Earned on {new Date(badge.earned_at).toLocaleDateString()}
            </p>
          )}

          {/* Dismiss button */}
          <button
            onClick={handleClose}
            className="w-full bg-dive-amber hover:bg-amber-600 text-white font-bold py-3 px-6 rounded-lg transition-all transform hover:scale-105 active:scale-95"
          >
            🎉 Awesome!
          </button>
        </div>

        {/* Glow effect */}
        <div className="absolute -inset-1 bg-dive-amber/20 blur-xl rounded-2xl -z-10" />
      </div>
    </div>
  );
}

export default BadgeCelebration;

